import { useState, useEffect } from "react";
import { useSelector } from "react-redux";
import {
    TOTAL_NUMBER_OF_CARDS_TO_SHOW,
    NUMBER_OF_MORE_LIKE_THIS_TO_SHOW,
    TOTAL_NUMBER_OF_LOADING_CARD_TO_SHOW,
    PROMISE_NAMES,
    DEFAULT_BASE_LAYOUT,
} from "./constants";
import { NUMBER_OF_RECOMMENDATIONS_TO_FETCH } from "../../../../components/VideoRecommendations/constants";
import LayoutFactory from "../../../../components/LayoutFactory/component";
import { SectionHeading } from "../../../../components/Heading/component";
import { getPlaceholderItems } from "../../../../components/LayoutFactory/component";
import { CardVariantsEnum } from "../../../../utils/dataTransform/ContentQueryWidget/types";
import { fetchMoreLikeThis } from "./utils";
import { fetchVideoRecommendations } from "../../../../components/VideoRecommendations/utils";
import useMounted from "../../../../hooks/useMounted";
import log from "../../../../utils/logger/library";
import { getABTestsConfig } from "../../../../actions/Page/utils";
import { getSelection } from "../../../../hooks/useABTest/utils";

const Loading = ({ isLoading }) => {
    if (!isLoading) return null;

    return (
        <div className="engagement-videos-loading">
            <div className="loading-headline" />
            <LayoutFactory
                layout={DEFAULT_BASE_LAYOUT}
                items={getPlaceholderItems(
                    TOTAL_NUMBER_OF_LOADING_CARD_TO_SHOW,
                    CardVariantsEnum.HORIZONTAL
                )}
            />
        </div>
    );
};

const VideoMoreLikeThis = ({ moreLikeThisClips, moreLikeThisLayout }) => {
    if (!moreLikeThisClips?.length) return;

    const featureAttributionData = {
        instance: "related-media",
        name: "vertical-card-list",
    };

    return (
        <div className="moreLikeThis" data-testid="video-more-like-this">
            <SectionHeading text="More Like This" />
            <LayoutFactory
                layout={moreLikeThisLayout}
                items={moreLikeThisClips}
                featureAttributionData={featureAttributionData}
            />
        </div>
    );
};

const VideoRecommendations = ({
    recommendedClips,
    recommendationsLayout,
    numberOfRecsToShow,
}) => {
    if (!recommendedClips?.length) return;

    const featureAttributionData = {
        name: "vertical-card-list",
        instance: "rec-sys",
    };

    return (
        <div
            className="video-recommendations"
            data-testid="video-recommendations"
        >
            <SectionHeading text="Recommended for You" />
            <LayoutFactory
                layout={recommendationsLayout}
                items={recommendedClips.slice(0, numberOfRecsToShow)}
                featureAttributionData={featureAttributionData}
            />
        </div>
    );
};

const EngagementVideos = ({ sourceId }) => {
    const path = useSelector((state) => state.app.path);
    const [isLoading, setIsLoading] = useState(true);
    const [moreLikeThisClips, setMoreLikeThisClips] = useState([]);
    const [recommendedClips, setRecommendedClips] = useState([]);
    const isMounted = useMounted();

    const numberOfRecsToShow =
        TOTAL_NUMBER_OF_CARDS_TO_SHOW - moreLikeThisClips?.length;

    const moreLikeThisLayout = {
        ...DEFAULT_BASE_LAYOUT,
        rows: NUMBER_OF_MORE_LIKE_THIS_TO_SHOW,
    };

    const recommendationsLayout = {
        ...DEFAULT_BASE_LAYOUT,
        rows: numberOfRecsToShow,
    };

    const parseAllSettled = (name, promise) => {
        if (promise.status === "fulfilled") {
            return promise.value;
        } else {
            log.error(
                `(EngagementVideos) fetch ${name} failed: ${promise.reason}`
            );
            return [];
        }
    };

    const getABTestAlgorithmId = async (configKey) => {
        const abTestsConfig = await getABTestsConfig();
        const { id, variants } = abTestsConfig?.[configKey] ?? {};
        const selection = await getSelection(id);
        return variants?.[selection]?.algorithmId ?? null;
    };

    const fetchEngagementVideosData = async () => {
        let moreLikeThisItems = [];
        let recommendedItems = [];

        const moreLikeThisAlgorithmId = await getABTestAlgorithmId(
            "video-page-related-links"
        );
        const recommendationsAlgorithmId = await getABTestAlgorithmId(
            "video-page-recsys"
        );

        try {
            const [moreLikeThisPromise, recommendationsPromise] =
                await Promise.allSettled([
                    fetchMoreLikeThis(
                        NUMBER_OF_MORE_LIKE_THIS_TO_SHOW,
                        sourceId,
                        path,
                        moreLikeThisLayout,
                        moreLikeThisAlgorithmId
                    ),
                    fetchVideoRecommendations(
                        NUMBER_OF_RECOMMENDATIONS_TO_FETCH,
                        path,
                        sourceId,
                        recommendationsLayout,
                        recommendationsAlgorithmId
                    ),
                ]);

            moreLikeThisItems = parseAllSettled(
                PROMISE_NAMES.MORE_LIKE_THIS,
                moreLikeThisPromise
            );
            recommendedItems = parseAllSettled(
                PROMISE_NAMES.RECOMMENDATIONS,
                recommendationsPromise
            );
        } catch {
            // promise.allSettled does not throw
        }

        setMoreLikeThisClips(moreLikeThisItems);
        setRecommendedClips(recommendedItems);
        setIsLoading(false);
    };

    const resetEngagementVideosState = () => {
        setIsLoading(true);
        setMoreLikeThisClips([]);
        setRecommendedClips([]);
    };

    useEffect(() => {
        if (!isMounted) return;

        if (!sourceId) {
            resetEngagementVideosState();
        } else {
            fetchEngagementVideosData();
        }
    }, [isMounted, sourceId]);

    return (
        <div className="engagement-videos">
            <Loading isLoading={isLoading} />
            <VideoMoreLikeThis
                moreLikeThisClips={moreLikeThisClips}
                moreLikeThisLayout={moreLikeThisLayout}
            />
            <VideoRecommendations
                recommendedClips={recommendedClips}
                recommendationsLayout={recommendationsLayout}
                numberOfRecsToShow={numberOfRecsToShow}
            />
        </div>
    );
};

export default EngagementVideos;
